// React imports
import { useState, useEffect, useMemo, useCallback } from "react";

// React Router imports
import { useLocation, useNavigate } from "react-router-dom";

// React Hook Form imports
import { useForm, useFieldArray } from "react-hook-form";

// Material UI imports
import { Box, FormControlLabel, Grid, RadioGroup } from "@mui/material";
import { Add } from "@mui/icons-material";

// Third-party imports
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { MRT_ColumnDef, MRT_RowData } from "material-react-table";
import { useTranslation } from "react-i18next";
import { enqueueSnackbar } from "notistack";

// ERP Common imports
import DynamicSectionHOC from "@/components/form-control/form-builder/form-builder-element/section";
import Button from "@/components/form-control/form-builder/form-builder-element/button";
import TabBar from "@/components/tabs/tabs";
import images from "@/assets/images";
import { Pathname } from "@/constants";
import FormHeader from "@/components/form-header/form-header";
import Typography from "@/components/typography/typography";
import formBuilderConversion from "@/utils/form-builder-conversion";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import { formatLabel } from "@/utils/format-text";
import Toast from "@/components/toast/toast";
import Radio from "@/components/radio/radio";
import { SECTION_TYPES } from "@/utils/constant";
import ErpLoader from "@/components/loaders/erp-loader";
import { getErrorMessage } from "@/utils/common";
import FormParser from "@/components/form-control/form-parser/form-parser";
import { Eye, EyeOff } from "@/components/icons";

// Redux actions
import {
  patchFormDataById,
  getFormDataById,
  getFieldsByFormId,
} from "../form-builder/redux/actionCreator";

// Local imports
import {
  IProperty,
  ISectionProperty,
  ISelectProperty,
  element,
} from "./field_properties";
import TextEditForm from "./element-edit-forms/text";
import SelectEditForm from "./element-edit-forms/select";
import SectionEditForm from "./element-edit-forms/section";
import CheckboxEditForm from "./element-edit-forms/checkbox";
import TableEditForm from "./element-edit-forms/table-form";
import FormFields from "./form-fields";
import { generateModulePath, useFormDataEffect } from "./utils/common";
import "./custom-form.scss";
import { resetState } from "./redux";

export interface IElementJson extends IProperty {
  type: string;
}

const FIELD_TYPES = {
  TEXT: 'text',
  NUMBER: 'number',
  EMAIL: 'email',
  PHONE: 'phone',
  SELECT: 'select',
  DATE: 'date',
  TIME: 'time',
  URL: 'url',
  CURRENCY: 'currency',
  FILE: 'file',
  GEO_LOCATION: 'geoLocation',
  IMAGE: 'image',
  SECTION: 'section',
  INFO: 'info',
  RELATION: 'relation',
  CHECKBOX: 'checkbox',
  TOGGLE_BUTTON: 'toggleButton',
  TAGS_INPUT: 'tagsInput'
} as const;

const DEFAULT_GRID_COLUMNS = 12;
const DEFAULT_COLUMN_WIDTH = 2;
const DEFAULT_TAB_TITLE = 'data';

const isValidNumber = (value: any): boolean => !isNaN(Number(value)) && isFinite(Number(value));
const deepClone = (obj: T): T => JSON.parse(JSON.stringify(obj));
const generateUniqueId = (separator = ''): { uniqueStringId: string } => {
  return { uniqueStringId: `${Date.now()}${separator}${Math.random().toString(36).substr(2, 9)}` };
};


export function CustomForm({redirectionPath}:any) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const rowData = location.state ?? [];

  const formBuilder = useAppSelector((state) => state.formBuilder);

  const { control } = useForm({
    reValidateMode: "onBlur",
  });

  const {
    fields: members,
    append,
    remove,
    update,
    move,
    insert,
  } = useFieldArray({
    control,
    name: "members",
  });

  const {
    fields: section,
    append: appendSection,
    remove: removeSection,
    update: updateSection,
  } = useFieldArray({
    control,
    name: "section",
  });

  const [selectedHoc, setSelectedHoc] = useState("");
  const [activeSection, setActiveSection] = useState<number>();
  const [activeMember, setActiveMember] = useState<number>();

  const [formJSON, setFormJSON] = useState<object[]>([]);
  const [elementJSON, setElementJSON] = useState<any>(null);
  const [positionEditedElement, setPositionEditedElement] = useState<
    number | undefined
  >();

  const [sectionFormJSON, setSectionFormJSON] = useState<object[]>([]);
  const [sectionJSON, setSectionJSON] = useState<any>({});
  const [sectionPositionEditedElement, setSectionPositionEditedElement] =
    useState<number | undefined>();
  const [removeSectionActionPerformed, setRemoveSectionActionPerformed] =
    useState(false);
  const [removeSectionMembersPerformed, setRemoveSectionMembersPerformed] =
    useState(false);
  const [removeSectionPosition, setRemoveSectionPosition] = useState<
    number | undefined
  >();
  const [initialLoading, setInitialLoading] = useState(true);
  const [tabsData, setTabsData] = useState<any[]>([]);
  const [activeTabValue, setActiveTabValue] = useState<number | undefined>(0);

  const [elementSwap, setElementSwap] = useState(false);
  const [modifyAccordion, setModifyAccordion] = useState(false);
  const [toast, setToast] = useState<IToast | null>(null);
  const [previewFieldsData, setPreviewFields] = useState<any>(null);
  const [_, setFormParserMethods] = useState<any>();

  let elementPerColumn: any;

  const currentModule = location.pathname.split('/')[2]

  const path =redirectionPath?redirectionPath: generateModulePath(currentModule)

  useFormDataEffect(
    formBuilder,
    tabsData,
    activeTabValue,
    {
      removeSection,
      remove,
      setTabsData,
      append,
      appendSection
    }
  );

  const pickPropertyEditForm = useCallback((type: string): React.ReactElement => {
    try {
      if (!type || typeof type !== 'string') {
        console.warn('Invalid field type provided to pickPropertyEditForm');
        return <div className="text-gray-500 p-4">Select a field to edit</div>;
      }

      const normalizedType = type.toLowerCase().trim();

      // Define form type groups for better maintainability
      const formGroups = {
        text: [
          'text', 'number', 'email', 'phone', 'date', 'time',
          'url', 'currency', 'file', 'geolocation', 'image',
          'relation', 'info', 'tagsinput'
        ],
        select: ['select'],
        section: ['section'],
        checkbox: ['checkbox', 'togglebutton', 'radiobutton'],
        table: ['table']
      };

      // Common props for reusability
      const commonTextProps = {
        element: elementJSON,
        handlePropertyValueUpdate,
        handleWidthChange,
        handleColumnSelect,
        fields: formBuilder?.fields || []
      };

      const commonSelectProps = {
        element: elementJSON,
        handlePropertyValueUpdate,
        handleSelectOptionValueUpdate,
        handleWidthChange,
        handleColumnSelect
      };

      // Determine form type and render appropriate component
      if (formGroups.text.includes(normalizedType)) {
        return (
          <TextEditForm
            {...commonTextProps}
            key={`text-form-${elementJSON?.id || 'default'}`}
          />
        );
      }

      if (formGroups.select.includes(normalizedType)) {
        return (
          <SelectEditForm
            {...commonSelectProps}
            key={`select-form-${elementJSON?.id || 'default'}`}
          />
        );
      }

      if (formGroups.section.includes(normalizedType)) {
        return (
          <SectionEditForm
            element={sectionJSON}
            handlePropertyValueUpdate={handleSectionPropertyValueUpdate}
            key={`section-form-${sectionJSON?.id || 'default'}`}
          />
        );
      }

      if (formGroups.checkbox.includes(normalizedType)) {
        return (
          <CheckboxEditForm
            {...commonSelectProps}
            key={`checkbox-form-${elementJSON?.id || 'default'}`}
          />
        );
      }

      if (formGroups.table.includes(normalizedType)) {
        return (
          <TableEditForm
            element={elementJSON}
            handlePropertyValueUpdate={handlePropertyValueUpdate}
            handleColumnsUpdate={handleColumnsUpdate}
            fields={formBuilder?.fields || []}
            key={`table-form-${elementJSON?.id || 'default'}`}
          />
        );
      }

      // Default fallback with helpful message
      console.warn(`Unknown field type: ${type}`);
      return (
        <div className="text-gray-500 p-4 border border-gray-200 rounded">
          <p className="font-medium">Unknown Field Type</p>
          <p className="text-sm mt-1">
            Field type "{type}" is not supported. Please select a valid field to edit.
          </p>
        </div>
      );

    } catch (error) {
      console.error('Error in pickPropertyEditForm:', error);
      return (
        <div className="text-red-500 p-4 border border-red-200 rounded bg-red-50">
          <p className="font-medium">Error Loading Form</p>
          <p className="text-sm mt-1">
            There was an error loading the property edit form. Please try again.
          </p>
        </div>
      );
    }
  }, [elementJSON, sectionJSON, formBuilder]);

  const addNewSection = useCallback((): void => {
    try {
      if (!element?.section) {
        console.error('Element section template is not available');
        return;
      }

      const { uniqueStringId: id } = generateUniqueId('-');
      const newSection: ISectionProperty = {
        ...deepClone(element.section),
        id: id,
      };

      // Update switcher forms with new ID if they exist
      if (newSection.switcher_forms) {
        newSection.switcher_forms = newSection.switcher_forms.map(form => ({
          ...form,
          id: generateUniqueId('-').uniqueStringId
        }));
      }

      const newSectionIndex = section.length;

      appendSection(newSection);
      setActiveSection(newSectionIndex);
      setSelectedHoc('section');

      const sectionData: ISectionProperty = {
        field_type: newSection.field_type,
        section_type: newSection.section_type,
        switcher_forms: newSection.switcher_forms,
        form_switcher_label: newSection.form_switcher_label,
        form_switcher_name: newSection.form_switcher_name,
        label: newSection.label,
        is_accordion: newSection.is_accordion,
        id: newSection.id ?? newSectionIndex,
      };

      setSectionJSON(sectionData);
      setSectionPositionEditedElement(newSectionIndex);
      pickPropertyEditForm(newSection.field_type);
    } catch (error) {
      console.error('Error adding new section:', error);
    }
  }, [element, section, appendSection, setActiveSection, setSelectedHoc, setSectionJSON, setSectionPositionEditedElement, pickPropertyEditForm]);

  /**
   * Enhanced handleAddTab with better validation and error handling
   */
  const handleAddTab = useCallback((): void => {
    try {
      const formWithMembers = deepClone(sectionFormJSON);

      // Populate sections with their members
      formWithMembers.forEach((section: ISectionProperty, sectionIndex: number) => {
        section.members = members.filter(
          (member: IProperty) => member?.section_order === sectionIndex
        );
      });

      const hasSystemMembers = formWithMembers.some((section: ISectionProperty) =>
        section.members?.some((member: IProperty) => Boolean(member?.is_system_field))
      );

      const shouldRemoveTab = !tabsData?.length && hasSystemMembers;

      const newTab: TabData = {
        tab_order: tabsData.length + 1,
        title: DEFAULT_TAB_TITLE,
        remove_tab: shouldRemoveTab,
        data: tabsData?.length ? [] : formWithMembers
      };

      setTabsData(prevTabs => [...prevTabs, newTab]);
    } catch (error) {
      console.error('Error adding new tab:', error);
    }
  }, [sectionFormJSON, members, tabsData, setTabsData]);

  const handleEditTabTitle = useCallback((tabId: number, newTitle: string): void => {
    try {
      if (!isValidNumber(tabId) || !newTitle?.trim()) {
        console.error('Invalid tab ID or title provided');
        return;
      }

      setTabsData(prevTabs =>
        prevTabs.map((tab: TabData) =>
          tab.tab_order === tabId ? { ...tab, title: newTitle.trim() } : tab
        )
      );
    } catch (error) {
      console.error('Error editing tab title:', error);
    }
  }, [setTabsData]);

  const handleRemoveTab = useCallback((id: number): void => {
    try {
      if (!isValidNumber(id)) {
        console.error('Invalid tab ID provided');
        return;
      }

      const filteredTabs = tabsData.filter((tab: TabData) => tab.tab_order !== id);

      // Reorder remaining tabs
      const reorderedTabs = filteredTabs.map((tab: TabData) => ({
        ...tab,
        tab_order: tab.tab_order > id ? tab.tab_order - 1 : tab.tab_order,
      }));

      setTabsData(reorderedTabs);
      if (activeTabValue == id) {
        setActiveTabValue(reorderedTabs?.length - 1)
      }

      if (reorderedTabs.length > 0) {
        const activeTab = reorderedTabs.find(
          (tab: TabData) => tab.tab_order === (activeTabValue ?? 0) + 1
        );

        if (activeTab?.data) {
          const sections = activeTab.data.map(
            ({ field_type, label, is_accordion, id, remove_section }) => ({
              field_type,
              label,
              is_accordion,
              id,
              remove_section,
            })
          );

          const membersList = activeTab.data.flatMap((section: ISectionProperty) =>
            section.members?.map((member: IProperty) => ({ ...member })) || []
          );

          // Clear current data and add new data
          remove();
          removeSection();
          membersList.forEach(member => append(member));
          sections.forEach(section => appendSection(section));
        }
      } else {
        // Clear all data if no tabs remain
        remove();
        removeSection();
      }
    } catch (error) {
      console.error('Error removing tab:', error);
    }
  }, [tabsData, activeTabValue, setTabsData, remove, removeSection, append, appendSection]);

  const handleElementSelection = useCallback((e: React.MouseEvent<HTMLElement>): void => {
    try {
      const target = e.target as HTMLElement;
      const elementPositionIndex = target.dataset.position;

      if (!elementPositionIndex || !isValidNumber(elementPositionIndex)) {
        console.error('Invalid element position index');
        return;
      }

      const positionIndex = Number(elementPositionIndex);

      if (positionIndex < 0 || positionIndex >= formJSON.length) {
        console.error('Element position index out of bounds');
        return;
      }

      setActiveMember(positionIndex);

      const elementDetails = deepClone(formJSON[positionIndex]);
      setPositionEditedElement(positionIndex);
      setElementJSON(elementDetails);
      setSelectedHoc('element');
    } catch (error) {
      console.error('Error selecting element:', error);
    }
  }, [formJSON, setActiveMember, setPositionEditedElement, setElementJSON, setSelectedHoc]);

  const handleSectionSelection = useCallback((e: React.MouseEvent<HTMLElement>): void => {
    try {
      const target = e.target as HTMLElement;
      const sectionPositionIndex = target.dataset.position;

      if (!sectionPositionIndex || !isValidNumber(sectionPositionIndex)) {
        console.error('Invalid section position index');
        return;
      }

      const positionIndex = Number(sectionPositionIndex);

      if (positionIndex < 0 || positionIndex >= sectionFormJSON.length) {
        console.error('Section position index out of bounds');
        return;
      }

      setActiveSection(positionIndex);

      const sectionDetails = deepClone(sectionFormJSON[positionIndex]);
      setSectionPositionEditedElement(positionIndex);
      setSectionJSON(sectionDetails);
      setSelectedHoc('section');
    } catch (error) {
      console.error('Error selecting section:', error);
    }
  }, [sectionFormJSON, setActiveSection, setSectionPositionEditedElement, setSectionJSON, setSelectedHoc]);

  const handleAddMember = useCallback((
    e: React.SyntheticEvent<HTMLElement>,
    otherProperties?: Record<string, any>
  ): void => {
    try {
      const target = e.currentTarget as HTMLElement;
      const sectionPositionIndex = target?.dataset?.position;

      if (sectionPositionIndex === undefined || !isValidNumber(sectionPositionIndex)) {
        console.error('Invalid section position index for adding member');
        return;
      }

      const positionIndex = Number(sectionPositionIndex);

      if (positionIndex < 0) {
        console.error('Section position index cannot be negative');
        return;
      }

      if (!element?.text) {
        console.error('Element text template is not available');
        return;
      }

      const newMember: IProperty = {
        ...deepClone(element.text),
        ...otherProperties,
        section_order: positionIndex,
        type: 'custom',
        is_new: true,
        custom_width: Math.floor(DEFAULT_GRID_COLUMNS / DEFAULT_COLUMN_WIDTH),
        is_fullwidth: false,
        is_system_field: Boolean(otherProperties?.is_system_field),
        id: generateUniqueId().uniqueStringId,
      };

      setActiveSection(positionIndex);
      append(newMember);

      const elementPositionIndex = members.length;
      setActiveMember(elementPositionIndex);
      setSelectedHoc('element');
      setPositionEditedElement(elementPositionIndex);
      setElementJSON(newMember);
      pickPropertyEditForm(newMember.field_type);
    } catch (error) {
      console.error('Error adding new member:', error);
    }
  }, [element, members, setActiveSection, append, setActiveMember, setSelectedHoc, setPositionEditedElement, setElementJSON, pickPropertyEditForm]);


  const handleSectionPropertyValueUpdate = useCallback((
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
    overrideValue?: any
  ): void => {
    try {
      const { name, type, checked, value } = e.target;

      if (!name) {
        console.error('Property name is required for update');
        return;
      }

      const updatedValue = overrideValue !== undefined ? overrideValue : (type === 'checkbox' ? checked : value);

      // Only update if value actually changed
      if (sectionJSON[name] !== updatedValue) {
        setSectionJSON(prev => ({ ...prev, [name]: updatedValue }));
      }
    } catch (error) {
      console.error('Error updating section property:', error);
    }
  }, [sectionJSON, setSectionJSON]);

  const handleWidthChange = useCallback((newCustomWidth: number): void => {
    try {
      if (!isValidNumber(newCustomWidth) || newCustomWidth < 1 || newCustomWidth > DEFAULT_GRID_COLUMNS) {
        console.error('Invalid width value provided');
        return;
      }

      setElementJSON(prevData => ({
        ...prevData,
        custom_width: newCustomWidth,
      }));
    } catch (error) {
      console.error('Error updating width:', error);
    }
  }, [setElementJSON]);

  const handleColumnSelect = useCallback((e: React.ChangeEvent<HTMLSelectElement>): void => {
    try {
      const columnValue = Number(e.target.value);

      if (!isValidNumber(columnValue) || columnValue <= 0) {
        console.error('Invalid column value provided');
        return;
      }

      const elementPerColumn = Math.floor(DEFAULT_GRID_COLUMNS / columnValue);

      if (elementPerColumn <= 0) {
        console.error('Calculated element per column is invalid');
        return;
      }

      // Batch update all members
      members.forEach((field: IProperty, index: number) => {
        update(index, { ...field, custom_width: elementPerColumn });
      });
    } catch (error) {
      console.error('Error updating column selection:', error);
    }
  }, [members, update]);

  const handlePropertyValueUpdate = useCallback((
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
    numberValue?: number,
    val?: string,
    fieldType?: keyof typeof FIELD_TYPES
  ): void => {
    try {
      const getValue = (): any => {
        if (fieldType) return fieldType;
        if (val !== undefined) return val;
        if (numberValue !== undefined) return numberValue;
        return e?.target?.value;
      };

      const value = getValue();
      const elementJSONCopy = deepClone(elementJSON);

      if (e?.target?.name) {
        elementJSONCopy[e.target.name] = e.target.type === 'checkbox' ? e.target.checked : value;
      }
      // Only update if there's an actual change
      if (fieldType) {
        const { uniqueStringId: uniqueId } = generateUniqueId();
        const copyOfUpdatedElement: IProperty = {
          ...element[fieldType],
          section_form_id: elementJSONCopy?.section_form_id,
          is_form_switcher_section: elementJSONCopy?.is_form_switcher_section,
          section_order: elementJSONCopy.section_order,
          id: uniqueId,
          type: 'custom',
          is_system_field: Boolean(elementJSONCopy?.is_system_field),
          is_new: true,
          column_width: elementJSONCopy?.column_width || 6,
          custom_width: elementJSONCopy?.custom_width || 6
        };
        setElementJSON(copyOfUpdatedElement);
      } else if (JSON.stringify(elementJSON) !== JSON.stringify(elementJSONCopy)) {
        setElementJSON({ ...elementJSON, ...elementJSONCopy });
      }
    } catch (error) {
      console.error('Error updating property value:', error);
    }
  }, [elementJSON, element, setElementJSON]);

  const handleSelectOptionValueUpdate = useCallback((options: any[]): void => {
    try {
      if (!Array.isArray(options)) {
        console.error('Options must be an array');
        return;
      }

      const elementJSONCopy = deepClone(elementJSON);
      elementJSONCopy.options = [...options];
      const { uniqueStringId: uniqueId } = generateUniqueId('-');

      if (JSON.stringify(elementJSON.options) !== JSON.stringify(options)) {
        setElementJSON({ ...elementJSON, ...elementJSONCopy, id: uniqueId });
      }
    } catch (error) {
      console.error('Error updating select options:', error);
    }
  }, [elementJSON, setElementJSON]);

  const handleColumnsUpdate = useCallback((columns: MRT_ColumnDef<MRT_RowData>[]): void => {
    try {
      if (!Array.isArray(columns)) {
        console.error('Columns must be an array');
        return;
      }

      const elementJSONCopy = deepClone(elementJSON);
      elementJSONCopy.table_columns = [...columns];
      const { uniqueStringId: uniqueId } = generateUniqueId('-');

      if (JSON.stringify(elementJSON.table_columns) !== JSON.stringify(columns)) {
        setElementJSON({ ...elementJSON, ...elementJSONCopy, id: uniqueId });
      }
    } catch (error) {
      console.error('Error updating table columns:', error);
    }
  }, [elementJSON, setElementJSON]);

  const handleRemoveSection = useCallback((sectionIndex: number): void => {
    try {
      if (!isValidNumber(sectionIndex) || sectionIndex < 0 || sectionIndex >= section.length) {
        console.error('Invalid section index for removal');
        return;
      }

      setRemoveSectionPosition(sectionIndex);
      removeSection(sectionIndex);

      // Remove associated members in reverse order to maintain indices
      const membersToRemove: number[] = [];
      members.forEach((member: IProperty, index: number) => {
        if (member?.section_order === sectionIndex) {
          membersToRemove.push(index);
        }
      });

      // Remove members in reverse order to maintain correct indices
      membersToRemove.reverse().forEach(memberIndex => {
        remove(memberIndex);
      });

      setRemoveSectionActionPerformed(true);
    } catch (error) {
      console.error('Error removing section:', error);
    }
  }, [section, members, setRemoveSectionPosition, removeSection, remove, setRemoveSectionActionPerformed]);

  const handleMakeAccordion = useCallback((sectionIndex: number): void => {
    try {
      if (!isValidNumber(sectionIndex) || sectionIndex < 0 || sectionIndex >= section.length) {
        console.error('Invalid section index for accordion conversion');
        return;
      }

      setModifyAccordion(true);
      setSectionPositionEditedElement(sectionIndex);
      setActiveSection(sectionIndex);

      const copyOfSection: ISectionProperty = {
        ...deepClone(section[sectionIndex]),
        is_accordion: true
      };

      updateSection(sectionIndex, copyOfSection);
      setSelectedHoc('section');
      setSectionJSON(copyOfSection);
      pickPropertyEditForm(copyOfSection.field_type);
    } catch (error) {
      console.error('Error making section accordion:', error);
    }
  }, [section, setModifyAccordion, setSectionPositionEditedElement, setActiveSection, updateSection, setSelectedHoc, setSectionJSON, pickPropertyEditForm]);

  const handleTabData = useCallback((): void => {
    try {
      const formWithMembers = deepClone(sectionFormJSON);

      formWithMembers.forEach((section: ISectionProperty, sectionIndex: number) => {
        section.members = members.filter(
          (member: IProperty) => member?.section_order === sectionIndex
        );
      });

      const hasSystemMembers = formWithMembers.some((section: ISectionProperty) =>
        section.members?.some((member: IProperty) => Boolean(member?.is_system_field))
      );

      const activeTabIndex = Number(activeTabValue);
      if (isValidNumber(activeTabIndex) && activeTabIndex >= 0 && activeTabIndex < tabsData.length) {
        const updatedTabsData = [...tabsData];
        updatedTabsData[activeTabIndex] = {
          ...updatedTabsData[activeTabIndex],
          data: formWithMembers,
          remove_tab: hasSystemMembers,
        };
        setTabsData(updatedTabsData)
        remove();
        removeSection();
        setActiveMember(undefined);
        setSelectedHoc("");
        setActiveSection(undefined);
      }
    } catch (error) {
      console.error('Error updating tab data:', error);
    }
  }, [sectionFormJSON, members, activeTabValue, tabsData, setTabsData]);

  // const handlePreview = () => {
  //   const formWithMembers = JSON.parse(JSON.stringify(sectionFormJSON));

  //   formWithMembers.forEach(
  //     (section: ISectionProperty, sectionIndex: number) => {
  //       section.members = members.filter(
  //         (member: any) => member?.section_order === sectionIndex,
  //       ) as (IProperty | ISelectProperty)[];
  //     },
  //   );
  //   const state = tabsData?.length
  //     ? { tab: tabsData }
  //     : { section: formWithMembers };

  //   const FormBuilderConversion = state && formBuilderConversion(state);

  //   setPreviewFields(FormBuilderConversion);
  // };

  // Option 1: Using useMemo to memoize the expensive computation
  const formWithMembers = useMemo(() => {
    if (!sectionFormJSON || !members) return null;

    return sectionFormJSON.map((section: ISectionProperty, sectionIndex: number) => ({
      ...section,
      members: members.filter(
        (member: any) => member?.section_order === sectionIndex,
      ) as (IProperty | ISelectProperty)[]
    }));
  }, [sectionFormJSON, members]);

  const previewState = useMemo(() => {
    if (!formWithMembers) return null;

    return tabsData?.length
      ? { tab: tabsData }
      : { section: formWithMembers };
  }, [tabsData, formWithMembers]);

  const previewFields = useMemo(() => {
    if (!previewState) return null;
    return formBuilderConversion(previewState);
  }, [previewState]);


  // Option 2: Optimized handlePreview function using useCallback
  const showPreview = useCallback(() => {
    if (previewFields) {
      setPreviewFields(previewFields);
    }
  }, [previewFields, setPreviewFields]);
  const hidePreview = useCallback(() => {
    setPreviewFields(null);
  }, []);

  // Enhanced handleFormUpdate with proper error handling and validation
  const handleFormUpdate = useCallback(async (): void => {
    try {



      // Early validation checks
      if (!sectionFormJSON || !Array.isArray(sectionFormJSON)) {
        console.warn('Invalid sectionFormJSON provided');
        return;
      }

      if (!members || !Array.isArray(members)) {
        console.warn('Invalid members array provided');
        return;
      }

      if (!rowData?._id) {
        console.warn('Missing rowData._id for form update');
        return;
      }

      // Deep clone with proper error handling
      let formWithMembers: IEnhancedSectionProperty[];

      try {
        formWithMembers = structuredClone(sectionFormJSON);
      } catch (cloneError) {
        // Fallback to JSON method if structuredClone is not available
        formWithMembers = JSON.parse(JSON.stringify(sectionFormJSON));
      }

      // Enhanced member filtering with better type safety and validation
      formWithMembers.forEach((section: IEnhancedSectionProperty, sectionIndex: number) => {
        // Validate section object
        if (!section || typeof section !== 'object') {
          console.warn(`Invalid section at index ${sectionIndex}`);
          return;
        }

        // Filter members with proper type checking and validation
        section.members = members.filter((member: IMember) => {
          // Validate member object and section_order
          return (
            member &&
            typeof member === 'object' &&
            typeof member.section_order === 'number' &&
            member.section_order === sectionIndex
          );
        }) as (IProperty | ISelectProperty)[];
      });

      const hasSystemMembers = formWithMembers.some((section: ISectionProperty) =>
        section.members?.some((member: IProperty) => Boolean(member?.is_system_field))
      );

      const activeTabIndex = Number(activeTabValue);
      let updatedTabsData = [...tabsData];
      if (isValidNumber(activeTabIndex) && activeTabIndex >= 0 && activeTabIndex < tabsData.length) {
        updatedTabsData = [...tabsData];
        updatedTabsData[activeTabIndex] = {
          ...updatedTabsData[activeTabIndex],
          data: formWithMembers,
          remove_tab: hasSystemMembers,
        };

        setTabsData(updatedTabsData)
      }

      // Build state object with proper validation
      const state: IFormState = updatedTabsData?.length > 0
        ? { tab: updatedTabsData }
        : { section: formWithMembers };

      // Validate formBuilderConversion function exists
      if (typeof formBuilderConversion !== 'function') {
        console.error('formBuilderConversion is not a function');
        return;
      }

      // Execute conversion with error handling
      let formBuilderConversionResult: any;
      try {
        formBuilderConversionResult = formBuilderConversion(state);
      } catch (conversionError) {
        console.error('Error during form builder conversion:', conversionError);
        return;
      }

      // Validate conversion result
      if (!formBuilderConversionResult) {
        console.warn('Form builder conversion returned empty result');
        return;
      }

      // Validate elementPerColumn
      // if (typeof elementPerColumn !== 'number' || elementPerColumn <= 0) {

      //   console.warn('Invalid elementPerColumn value');
      //   return;
      // }

      // Prepare payload with validation
      const updatePayload: IFormUpdatePayload = {
        gridCol: elementPerColumn || 2,
        fields: formBuilderConversionResult
      };

      // Dispatch update action
      const updateRes = await dispatch(
        patchFormDataById({
          id: rowData._id,
          data: updatePayload
        })
      );
      if (updateRes.meta.requestStatus === 'rejected') {

        const m = getErrorMessage(updateRes?.error?.message);
        enqueueSnackbar(m, { variant: 'error' });
      } else {
        enqueueSnackbar("Form updated successfully", { variant: 'success' });
        navigate(path);
      }

    } catch (error) {
      const m = error?.message || 'Something went wrong while updating the form';
      enqueueSnackbar(m, { variant: 'error' });
    }
  }, [
    sectionFormJSON,
    members,
    tabsData,
    rowData?._id,
    elementPerColumn,
    dispatch,
    formBuilderConversion
  ]);

  const handleDuplicateElement = (index: number) => {
    const copyOfMember: any = { ...members[index], type: "custom" };
    copyOfMember && insert(index + 1, copyOfMember);
    setActiveMember(index + 1);
    setSelectedHoc("element");
    setPositionEditedElement(index + 1);
    setElementJSON(copyOfMember);
    pickPropertyEditForm(copyOfMember.field_type);
  };

  const breadCrumbPath = [
    {
      link: path,
      label: "Forms",
    },
    {
      link: "",
      label: formatLabel(rowData?.name),
    },
    {
      link: "",
      label: "Edit Form",
    },
  ];


  useEffect(() => {
    if (removeSectionActionPerformed) {
      members.forEach((member: any, index: number) => {
        if (
          removeSectionPosition !== undefined &&
          Boolean(removeSectionPosition?.toString) &&
          member.section_order > removeSectionPosition
        ) {
          update(index, { ...member, section_order: member.section_order - 1 });
        }
      });
      setRemoveSectionActionPerformed(false);
      setRemoveSectionMembersPerformed(true);
      setRemoveSectionPosition(undefined);
      setSelectedHoc("");
    }

    // eslint-disable-next-line
  }, [removeSectionActionPerformed]);

  useEffect(() => {
    formBuilder.isSuccess && navigate(path);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formBuilder.isSuccess]);

  useEffect(() => {
    if (formBuilder.isError) {
      setToast({
        type: 'alert',
        message: formBuilder.error
      });
    }
  }, [formBuilder.error, formBuilder.isError]);

  useEffect(() => {
    if (
      typeof positionEditedElement !== "number" ||
      typeof positionEditedElement !== "string"
    ) {
      const index = Number(positionEditedElement);
      if (formJSON[index]) {
        const copyOfUpdatedFormJson = JSON.parse(
          JSON.stringify(formJSON[index]),
        );
        update(index, { ...copyOfUpdatedFormJson });
      }
    }
    // eslint-disable-next-line
  }, [formJSON]);

  useEffect(() => {
    const updatedFormJSON = formJSON.map((obj: any, index: number) => {
      if (index === Number(positionEditedElement)) {
        return { ...elementJSON };
      } else {
        return { ...obj };
      }
    });
    setFormJSON(updatedFormJSON);

    // eslint-disable-next-line
  }, [elementJSON]);

  useEffect(() => {
    if (members.length !== formJSON.length) {
      setFormJSON(JSON.parse(JSON.stringify(members)));
    }
    if (
      Boolean(elementSwap) &&
      JSON.stringify(members) !== JSON.stringify(formJSON)
    ) {
      setFormJSON(JSON.parse(JSON.stringify(members)));
      setElementSwap(false);
      const newMember: any =
        positionEditedElement && members[positionEditedElement];
      newMember && setSelectedHoc("element");
      newMember && setElementJSON(newMember);
      newMember && pickPropertyEditForm(newMember.field_type);
    }
    if (
      removeSectionMembersPerformed &&
      JSON.stringify(members) !== JSON.stringify(formJSON)
    ) {
      setFormJSON(JSON.parse(JSON.stringify(members)));
      setRemoveSectionMembersPerformed(false);
    }
    // eslint-disable-next-line
  }, [members, elementSwap]);

  useEffect(() => {
    if (
      typeof sectionPositionEditedElement !== "number" ||
      typeof sectionPositionEditedElement !== "string"
    ) {
      const index = Number(sectionPositionEditedElement);
      if (sectionFormJSON[index]) {
        const copyOfUpdatedFormJson = JSON.parse(
          JSON.stringify(sectionFormJSON[index]),
        );
        updateSection(index, { ...copyOfUpdatedFormJson });
      }
    }
    // eslint-disable-next-line
  }, [sectionFormJSON]);

  useEffect(() => {
    const updatedFormJSON = sectionFormJSON.map((obj: any, index: number) => {
      if (index === Number(sectionPositionEditedElement)) {
        return { ...obj, ...sectionJSON };
      } else {
        return { ...obj };
      }
    });
    setSectionFormJSON(updatedFormJSON);
    // eslint-disable-next-line
  }, [sectionJSON]);

  useEffect(() => {
    if (section.length !== sectionFormJSON.length) {
      setSectionFormJSON(JSON.parse(JSON.stringify(section)));
    }
    if (
      JSON.stringify(sectionFormJSON) !== JSON.stringify(section) &&
      modifyAccordion
    ) {
      setSectionFormJSON(JSON.parse(JSON.stringify(section)));
      setModifyAccordion(false);
    }
    // eslint-disable-next-line
  }, [section]);

  useEffect(() => {
    () => setInitialLoading(true);
    return () => {
      setInitialLoading(false);
      dispatch(resetState())
    }
  }, [])

  const handleFormData = useCallback(async () => {
    setInitialLoading(true)
    try {
      if (rowData?._id) {
        setTabsData([])
        await dispatch(getFormDataById(rowData?._id));
        await dispatch(getFieldsByFormId({ id: rowData?._id }));

      }
    } finally {
      setInitialLoading(false);
    }

  }, [rowData?._id])

  useEffect(() => {
    handleFormData();
  }, [handleFormData]);

  type SelectedHoc = "element" | "section" | null;

  // Constants
  const DEFAULT_FIELD_TYPE = "text";
  const HOC_TYPES = {
    ELEMENT: "element",
    SECTION: "section"
  } as const;

  // Helper function to check if object has meaningful content
  const hasValidContent = (obj: Record<string, any>): boolean => {
    return obj && Object.keys(obj).length > 0;
  };

  const EmptyStateMessage = () => (
    <Typography type="s5" color={"theme.secondary.800"} className="formSection--Text">Select a field to edit its properties.</Typography>
  )

  // Helper function to get field type with fallback
  const getFieldTypeWithFallback = (
    json: ElementJSON | SectionJSON | null | undefined,
    fallback: string = DEFAULT_FIELD_TYPE
  ): string => {
    return json?.field_type || fallback;
  };

  const memoizedForm = useMemo(() => {
    // Early return for empty state
    if (!selectedHoc) {

      return <EmptyStateMessage />;
    }

    // Handle element selection
    if (selectedHoc === HOC_TYPES.ELEMENT) {
      if (!hasValidContent(members)) {
        console.warn('Element selected but no members data available');
        return <EmptyStateMessage />;
      }

      const fieldType = getFieldTypeWithFallback(elementJSON);
      return pickPropertyEditForm(fieldType);
    }

    // Handle section selection
    if (selectedHoc === HOC_TYPES.SECTION) {
      if (!hasValidContent(section)) {
        console.warn('Section selected but no section data available');
        return <EmptyStateMessage />;
      }

      const fieldType = getFieldTypeWithFallback(sectionJSON);
      return pickPropertyEditForm(fieldType);
    }

    // Fallback for unknown selection type
    console.warn(`Unknown selectedHoc type: ${selectedHoc}`);
    return <EmptyStateMessage />;
  }, [
    // Core dependencies
    selectedHoc,

    // Data dependencies
    Object.keys(members).length,
    Object.keys(section).length,

    // JSON dependencies with deep comparison consideration
    elementJSON?.field_type,
    elementJSON?.field_properties,
    sectionJSON?.field_type,
    sectionJSON?.field_properties,

    // Function dependency
    pickPropertyEditForm
  ]);

  return (
    //   <FullScreenLayout className="formSection--Layout">
    <Box position='relative'>
      {initialLoading ? <ErpLoader /> :
        <DndProvider backend={HTML5Backend}>
          <FormHeader breadCrumbPath={breadCrumbPath}>
            <div className="formSection--Buttons">
              <Button
                variant="outlined"
                className="formSection--PreviewButton"
                text="Preview"
                onClick={() => { !previewFieldsData ? showPreview() : hidePreview() }}
                disable={!members?.length}
                startIcon={previewFieldsData ? <EyeOff /> : <Eye />}
              />
              <Button
                variant="contained"
                type="submit"
                text="Update"
                onClick={handleFormUpdate}
                disable={previewFieldsData}
              />
            </div>
          </FormHeader>
          {!previewFieldsData ?
            <Grid className="formSection--Grid" container>
              <Grid
                item
                xs={9}
                component="form"
                sx={{
                  position: "relative",
                  background: "#F5F6F5",
                  padding: "0.75rem",
                }}
              >
                <Grid className="formSection--GridContainer">
                  <Box className="formSection--Tab">
                    {tabsData && (
                      <TabBar
                        tabs={tabsData}
                        onEditTabTitle={handleEditTabTitle}
                        activeValue={activeTabValue}
                        setActiveValue={setActiveTabValue}
                        handleTabChange={() => {
                          handleTabData()
                        }}
                        handleRemoveTab={handleRemoveTab}
                        containerProps={{ mb: 0 }}
                      />
                    )}
                    <Button
                      variant="outlined"
                      text="Add Tab"
                      onClick={handleAddTab}
                      style={{ color: "#292D32", margin: "0.5rem" }}
                    />
                  </Box>
                  <div className="formSection--Form">
                    {section?.map((item: any, i: number) => {
                      const sectionMembers = members.filter(
                        (member: any) => member.section_order === i,
                      );
                      const isFormSwitcher = item.section_type === SECTION_TYPES.FORM_SWITCH_SECTION
                      const switchForms = item?.switcher_forms?.map((form: any) => ({
                        ...form,
                        formMembers: members.filter(
                          (member: any) => member.section_form_id === form.id,
                        )
                      }))

                      return (
                        <DynamicSectionHOC
                          index={i}
                          label={t(item.label)}
                          onClick={handleSectionSelection}
                          position={i}
                          fieldArrayName="section"
                          isFormSwitcher={isFormSwitcher}
                          activeIndex={activeSection === i}
                          removeSection={() => handleRemoveSection(i)}
                          handleAccordion={() => handleMakeAccordion(i)}
                          handleAddButton={(e) => handleAddMember(e)}
                          remove_section={item.remove_section}
                        >
                          {isFormSwitcher ? (
                            <div style={{ width: '100%' }} >
                              <Typography type="s5" style={{ fontWeight: 500 }} color={"theme.secondary.800"}>
                                {item.form_switcher_label}
                              </Typography>
                              <RadioGroup
                                aria-labelledby="theme-radio-buttons-group-label"
                                name={item.form_switcher_name || item.form_switcher_label}
                              >

                                {switchForms?.map(({ name, id, formMembers }: { name: string, id: number, formMembers: any }) => (
                                  <>
                                    <div>
                                      <FormControlLabel
                                        key={id}
                                        value={name}
                                        control={<Radio />}
                                        label={t(name)}
                                        disabled
                                      />
                                    </div>

                                    <Grid container sx={{ mb: 3 }}>
                                      <FormFields
                                        sectionMembers={formMembers || []}
                                        members={members}
                                        activeMember={activeMember}
                                        control={control}
                                        t={t}
                                        element={element}
                                        images={images}
                                        elementJSON={elementJSON}
                                        remove={remove}
                                        move={move}
                                        handleElementSelection={handleElementSelection}
                                        setElementSwap={setElementSwap}
                                        handleDuplicateElement={handleDuplicateElement}
                                        setSelectedHoc={setSelectedHoc}
                                        setActiveMember={setActiveMember}
                                      />
                                    </Grid>
                                    <Button
                                      text="Add"
                                      variant="outlined"
                                      className="formSection--PreviewButton"
                                      data-position={i}
                                      onClick={(e) => handleAddMember(e, {
                                        section_form_id: id,
                                        is_form_switcher_section: true
                                      })}
                                      startIcon={<Add />}
                                    />
                                  </>
                                ))}
                              </RadioGroup>
                            </div>
                          ) : null}
                          {!isFormSwitcher &&
                            <FormFields
                              sectionMembers={sectionMembers || []}
                              members={members}
                              activeMember={activeMember}
                              control={control}
                              t={t}
                              element={element}
                              images={images}
                              elementJSON={elementJSON}
                              remove={remove}
                              move={move}
                              handleElementSelection={handleElementSelection}
                              setElementSwap={setElementSwap}
                              handleDuplicateElement={handleDuplicateElement}
                              setSelectedHoc={setSelectedHoc}
                              setActiveMember={setActiveMember}
                            />
                          }
                        </DynamicSectionHOC>
                      );
                    })}
                    <div className="formSection--Section">
                      <Button
                        text="Add a section"
                        variant="outlined"
                        className="formSection--PreviewButton"
                        onClick={addNewSection}
                        startIcon={<Add />}
                      />
                    </div>
                  </div>
                </Grid>

              </Grid>
              <Grid
                item
                xs={3}
                className={
                  Boolean(selectedHoc) && (members?.length || section.length)
                    ? "formSection--RightGrid"
                    : "formSection--RightGrid formSection--Text"
                }
              >
                {memoizedForm}
              </Grid>
            </Grid>
            :
            <FormParser
              data={previewFieldsData || []}
              handleFormSubmit={(data) => console.log("Preview Data", data)}
              className='form-container'
              fieldArrayName='form-builder-preview'
              getFormMethods={(methods: any) =>
                setFormParserMethods({ ...methods })
              }
            />
          }
          <Toast
            open={Boolean(toast)}
            type={toast?.type}
            message={toast?.message}
            handleClose={() => setToast(null)}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          />
        </DndProvider>}
    </Box>
    //    </FullScreenLayout>
  );
}

export default CustomForm;
